import React from 'react';
import { Link } from 'react-router-dom';

// Opportunity stage config
const STAGE_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700', icon: '🆕' },
  active: { label: 'Active', color: 'bg-orange-100 text-orange-700', icon: '💬' },
  converted: { label: 'Converted', color: 'bg-green-100 text-green-700', icon: '✅' },
  inactive: { label: 'Inactive', color: 'bg-gray-100 text-gray-500', icon: '⏸️' },
};

export default function OpportunityCard({ opportunity }) {
  const stageConfig = STAGE_CONFIG[opportunity.stage] || STAGE_CONFIG.new;
  
  const formatCurrency = (value) => {
    if (!value) return null;
    return `£${parseFloat(value).toLocaleString()}`;
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  // Calculate total estimated value
  const estimatedTotal = (parseFloat(opportunity.estimatedInitialValue) || 0) + 
    (parseFloat(opportunity.estimatedMonthlyValue) || 0) * 12;
  
  // For converted deals, use actual values if available
  const isConverted = opportunity.stage === 'converted';
  const actualTotal = isConverted ? 
    (parseFloat(opportunity.actualInitialValue) || parseFloat(opportunity.estimatedInitialValue) || 0) + 
    (parseFloat(opportunity.actualMonthlyValue) || parseFloat(opportunity.estimatedMonthlyValue) || 0) * 12
    : null;

  return (
    <Link
      to={`/crm/opportunities/${opportunity.id}`}
      className="block border border-gray-200 rounded-lg p-4 hover:border-zapt-purple/50 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-gray-900 truncate">{opportunity.title}</h4>
            <span className={`text-xs px-2 py-0.5 rounded-full ${stageConfig.color}`}>
              {stageConfig.label}
            </span>
          </div>
          
          {/* Value display */}
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
            {opportunity.estimatedInitialValue && (
              <span title="Estimated initial value">
                Initial: {formatCurrency(isConverted && opportunity.actualInitialValue ? opportunity.actualInitialValue : opportunity.estimatedInitialValue)}
              </span>
            )}
            {opportunity.estimatedMonthlyValue && (
              <span title="Estimated monthly value">
                Monthly: {formatCurrency(isConverted && opportunity.actualMonthlyValue ? opportunity.actualMonthlyValue : opportunity.estimatedMonthlyValue)}
              </span>
            )}
          </div>

          {/* Next action */}
          {opportunity.nextAction && (
            <div className="text-sm">
              <span className="text-gray-500">Next:</span>{' '}
              <span className="text-gray-900">{opportunity.nextAction}</span>
              {opportunity.nextActionDue && (
                <span className={`ml-2 text-xs ${new Date(opportunity.nextActionDue) < new Date() ? 'text-red-600' : 'text-gray-500'}`}>
                  (Due: {formatDate(opportunity.nextActionDue)})
                </span>
              )}
            </div>
          )}

          {/* Linked app */}
          {opportunity.linkedAppName && (
            <div className="mt-2 text-xs text-gray-500">
              Linked to: {opportunity.linkedAppName}
            </div>
          )}
        </div>

        {/* Probability/Value badge */}
        <div className="text-right flex-shrink-0">
          {estimatedTotal > 0 && (
            <div className="text-lg font-semibold text-gray-900">
              {formatCurrency(isConverted && actualTotal ? actualTotal : estimatedTotal)}
            </div>
          )}
          {opportunity.probability !== null && opportunity.probability !== undefined && !isConverted && opportunity.stage !== 'inactive' && (
            <div className="text-xs text-gray-500">
              {opportunity.probability}% probability
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
